import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { HandHeart, Calendar, Activity, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { safeNumber } from "@/lib/formatters";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR");
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}
function monthLabel(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${months[parseInt(m, 10) - 1] || m}/${y}`;
}

function Kpi({ icon: Icon, label, value, tone = "emerald", testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-800",
    blue: "border-blue-200 bg-blue-50/40 text-blue-800",
    amber: "border-amber-200 bg-amber-50/40 text-amber-800",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone] || tones.emerald}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-display text-3xl font-bold mt-1">{fmt(value)}</div>
    </div>
  );
}

function RankCard({ title, rows, hrefBase, testid }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-semibold text-slate-900 text-sm">{title}</h3>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">Sem dados no período.</div>
        ) : (
          <table className="w-full text-sm" data-testid={testid}>
            <tbody>
              {rows.slice(0, 10).map((r, i) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-500 w-8">{i + 1}</td>
                  <td className="px-4 py-2">
                    {hrefBase ? (
                      <Link to={`${hrefBase}${r.id}`} className="text-slate-900 hover:text-blue-700">{r.name}</Link>
                    ) : <span className="text-slate-900">{r.name}</span>}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-emerald-700">{fmt(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/** Dedicated Pro Bono management page — KPIs + by-surgeon / by-procedure / by-month
 * rankings + a full list of pro bono cases. */
export default function ProBonoPage() {
  const today = new Date();
  const defaultFrom = `${today.getFullYear()}-01-01`;
  const defaultTo = today.toISOString().slice(0, 10);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [surgeons, setSurgeons] = useState([]);
  const [surgeonId, setSurgeonId] = useState("");
  const [hospitals, setHospitals] = useState([]);
  const [hospitalId, setHospitalId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (surgeonId) params.set("surgeon_id", surgeonId);
      if (hospitalId) params.set("hospital_id", hospitalId);
      const r = await api.get(`/reports/pro-bono?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar Pro Bono");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, surgeonId, hospitalId]);
  useEffect(() => { api.get("/surgeons").then((r) => setSurgeons(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/hospitals").then((r) => setHospitals(r.data || [])).catch(() => {}); }, []);

  const totals = data?.totals || { cases: 0, executed: 0, suspended: 0 };
  const rows = data?.cases || [];

  function setCurrentYear() {
    const y = new Date().getFullYear();
    setDateFrom(`${y}-01-01`); setDateTo(`${y}-12-31`);
  }
  function setCurrentMonth() {
    const d = new Date();
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0");
    setDateFrom(`${y}-${m}-01`);
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    setDateTo(`${y}-${m}-${String(last).padStart(2, "0")}`);
  }

  return (
    <div className="space-y-6" data-testid="pro-bono-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <HandHeart className="h-7 w-7 text-emerald-700" /> Pro Bono
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cirurgias beneficentes da equipe — contam na produção mas não geram faturamento.
          </p>
        </div>
        <Link to="/casos?pro_bono=true" className="text-sm font-medium text-blue-700 hover:underline flex items-center gap-1" data-testid="link-ver-casos">
          Ver todos os casos pro bono <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">De</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="w-full sm:w-44 mt-1" data-testid="filter-from" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Até</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="w-full sm:w-44 mt-1" data-testid="filter-to" />
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Cirurgião</Label>
              <select value={surgeonId} onChange={(e) => setSurgeonId(e.target.value)}
                className="w-full sm:w-56 mt-1 block border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
                data-testid="filter-surgeon">
                <option value="">Todos</option>
                {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hospital</Label>
              <select value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}
                className="w-full sm:w-56 mt-1 block border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white min-h-[44px]"
                data-testid="filter-hospital">
                <option value="">Todos</option>
                {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto w-full sm:w-auto">
              <Button variant="outline" type="button" onClick={setCurrentMonth} data-testid="btn-current-month" className="h-11 md:h-9 flex-1 sm:flex-initial">
                <Calendar className="h-4 w-4 mr-1" /> Mês atual
              </Button>
              <Button variant="outline" type="button" onClick={setCurrentYear} data-testid="btn-current-year" className="h-11 md:h-9 flex-1 sm:flex-initial">
                <Calendar className="h-4 w-4 mr-1" /> Ano atual
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi icon={HandHeart} label="Total Pro Bono" value={safeNumber(totals.cases)} tone="emerald" testid="kpi-total" />
        <Kpi icon={Activity} label="Executadas" value={safeNumber(totals.executed)} tone="blue" testid="kpi-executed" />
        <Kpi icon={XCircle} label="Suspensas (neutro)" value={safeNumber(totals.suspended)} tone="amber" testid="kpi-suspended" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ErrorBoundary inline label="Ranking por cirurgião indisponível">
          <RankCard title="Ranking por cirurgião" rows={data?.by_surgeon || []} testid="rank-surgeons" />
        </ErrorBoundary>
        <ErrorBoundary inline label="Ranking por procedimento indisponível">
          <RankCard title="Ranking por procedimento" rows={data?.by_procedure || []} testid="rank-procedures" />
        </ErrorBoundary>
      </div>

      <ErrorBoundary inline label="Evolução mensal indisponível">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="font-display font-semibold text-slate-900 text-sm">Evolução mensal</h3>
            </div>
            {(data?.by_month || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">Sem dados no período.</div>
            ) : (
              <div className="p-4">
                <div className="flex items-end gap-2 h-32" data-testid="chart-by-month">
                  {data.by_month.map((m) => {
                    const max = Math.max(...data.by_month.map((x) => x.total), 1);
                    const h = Math.round((m.total / max) * 100);
                    return (
                      <div key={m.ym} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <div className="text-[10px] text-slate-500 font-semibold">{m.total}</div>
                        <div className="w-full bg-emerald-500 rounded-t-sm" style={{ height: `${h}%`, minHeight: m.total > 0 ? "4px" : "1px" }} />
                        <div className="text-[10px] text-slate-500 -rotate-45 origin-top-left mt-2 whitespace-nowrap">{monthLabel(m.ym)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </ErrorBoundary>

      <ErrorBoundary inline label="Lista de casos indisponível">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-display font-semibold text-slate-900 text-sm">Casos pro bono no período</h3>
              <div className="text-xs text-slate-500">{loading ? "Carregando…" : `${rows.length} caso(s)`}</div>
            </div>
            {rows.length === 0 && !loading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Nenhuma cirurgia pro bono no período.</div>
            ) : (
              <table className="w-full text-sm" data-testid="pro-bono-cases-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2.5">Código</th>
                    <th className="text-left px-4 py-2.5">Data</th>
                    <th className="text-left px-4 py-2.5">Paciente</th>
                    <th className="text-left px-4 py-2.5">Procedimento</th>
                    <th className="text-left px-4 py-2.5">Cirurgião</th>
                    <th className="text-left px-4 py-2.5">Hospital</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-right px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c, idx) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`pro-bono-row-${idx}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{c.case_code}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(c.surgery_date)} {c.surgery_time && <span className="text-slate-400 text-xs">{c.surgery_time}</span>}</td>
                      <td className="px-4 py-2.5 font-medium text-slate-900">{c.patient_name}</td>
                      <td className="px-4 py-2.5">{c.procedure_name}</td>
                      <td className="px-4 py-2.5">{c.surgeon_name}</td>
                      <td className="px-4 py-2.5">{c.hospital_name}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider ${c.status === "suspensa" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}>
                          {c.status?.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link to={`/casos/${c.id}`} className="text-blue-700 hover:underline text-sm" data-testid={`btn-open-${idx}`}>Abrir</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </ErrorBoundary>
    </div>
  );
}
